import React, { useState } from 'react'
import { useTranslation } from '@/i18n'
import { submitFeedback } from '@/utils/api'

const FeedbackPage: React.FC = () => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    email: ''
  })
  const [errors, setErrors] = useState<string[]>([])
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  const validateForm = () => {
    const errors: string[] = []

    if (!formData.title.trim()) {
      errors.push(t('feedback.titleRequired'))
    } else if (formData.title.length > 100) {
      errors.push(t('feedback.titleTooLong'))
    }

    if (!formData.content.trim()) {
      errors.push(t('feedback.contentRequired'))
    } else if (formData.content.length > 1000) {
      errors.push(t('feedback.contentTooLong'))
    }

    if (!formData.email.trim()) {
      errors.push(t('feedback.emailRequired'))
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push(t('feedback.invalidEmail'))
    }

    setErrors(errors)
    return errors.length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      const result = await submitFeedback(formData)

      if (result.code === 200) {
        setSuccess(true)
        setFormData({ title: '', content: '', email: '' })
        setErrors([])
      } else {
        setErrors([result.message])
      }
    } catch (error: any) {
      setErrors([error.message || t('feedback.networkError')])
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6">{t('feedback.title')}</h2>

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
          {t('feedback.submitSuccess')}
        </div>
      )}

      {errors.length > 0 && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          <ul>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-2">
            {t('feedback.titleLabel')}
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-800 bg-gray-900 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            placeholder={t('feedback.titlePlaceholder')}
            maxLength={100}
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.title.length}/100
          </p>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
            {t('feedback.emailLabel')}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-800 bg-gray-900 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            placeholder={t('feedback.emailPlaceholder')}
          />
        </div>

        <div>
          <label htmlFor="content" className="block text-sm font-medium text-gray-300 mb-2">
            {t('feedback.contentLabel')}
          </label>
          <textarea
            id="content"
            name="content"
            value={formData.content}
            onChange={handleInputChange}
            rows={6}
            className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-800 bg-gray-900 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            placeholder={t('feedback.contentPlaceholder')}
            maxLength={1000}
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.content.length}/1000
          </p>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg px-6 py-2 font-medium transition-all duration-300 shadow-lg hover:shadow-xl text-white disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? t('common.loading') : t('feedback.submit')}
          </button>
        </div>
      </form>
    </div>
  )
}

export default FeedbackPage